import { useRef, useState, useEffect, useMemo } from 'react';

import { CardMedia, Grid } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

import AspectRatioPaddingBox from '@/components/AspectRatioPaddingBox';
import ImageWrapper from '@/components/ImageWrapper';

import { getPaddingBoxStyles, getIconStyles, getIconContainerStyles, getImageWrapperStyles } from './StaffCard.styles';

export default function StaffImage({ firstName, photoUrl }: { firstName?: string | null; photoUrl?: string | null }) {
	const [iconContainerHeight, setIconContainerHeight] = useState<number>(0);
	const [photoError, setPhotoError] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const resizeHeight = () => {
			if (ref.current) {
				setIconContainerHeight(ref.current.clientHeight);
			}
		};
		window.addEventListener('resize', resizeHeight);
		resizeHeight();
		return () => window.removeEventListener('resize', resizeHeight);
	}, [ref]);

	const src = useMemo(() => {
		if (photoUrl?.startsWith('https://cdn.bfldr.com')) {
			return `${photoUrl.split('?')[0]}?width=339&height=403&fit=cover`;
		}

		if (photoUrl) {
			return `${photoUrl.split('?')[0]}?crop=339,403`;
		}

		return '';
	}, [photoUrl]);

	return (
		<CardMedia ref={ref}>
			<AspectRatioPaddingBox
				placeholderColor="backgrounds.light"
				aspectRatio="1:1"
				paddingBoxSx={getPaddingBoxStyles}
				imageWrapperSx={getImageWrapperStyles}
			>
				{photoUrl && !photoError ? (
					<ImageWrapper
						data-test-id={`staff_image_${firstName || ''}`}
						maxHeight="100%"
						width="100%"
						height="100%"
						background-size="cover"
						background-repeat="no-repeat"
						background-position="center center"
						object-fit="cover"
						borderRadius="0.5rem"
						src={src}
						alt={`${firstName || ''} portrait`}
						onError={() => setPhotoError(true)}
					/>
				) : (
					<Grid
						data-test-id={`staff_image_default`}
						container
						justifyContent="center"
						alignItems="center"
						sx={getIconContainerStyles({ height: iconContainerHeight })}
					>
						<PersonOutlineIcon sx={getIconStyles} />
					</Grid>
				)}
			</AspectRatioPaddingBox>
		</CardMedia>
	);
}
